import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { Centered } from "./Login";

/* First run. Creates the family and its first grown-up.
 *
 * The server refuses this once a family exists, so there is no window where a
 * stranger who finds the URL can claim the instance.
 */
export function Setup({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({
    familyName: "",
    parentName: "",
    loginName: "",
    password: "",
    email: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function set(key: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [key]: e.target.value });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/setup", form);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Setup failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Centered>
      <h1 style={{ fontSize: 30, marginBottom: 6 }}>Set up your family</h1>
      <p style={{ color: "var(--ink-2)", margin: "0 0 26px" }}>
        You&rsquo;ll add the kids next.
      </p>
      <form onSubmit={submit} style={{ display: "grid", gap: 14, width: "100%", textAlign: "left" }}>
        <Field label="Family name">
          <input value={form.familyName} onChange={set("familyName")} required
                 placeholder="The Rivera family" />
        </Field>
        <Field label="Your name">
          <input value={form.parentName} onChange={set("parentName")} required
                 placeholder="Sam" />
        </Field>
        <Field label="Your sign-in name">
          <input value={form.loginName} onChange={set("loginName")} required
                 minLength={2} autoCapitalize="none" placeholder="sam" />
        </Field>
        <Field label="Password" hint="At least 8 characters.">
          <input type="password" value={form.password} onChange={set("password")}
                 required minLength={8} />
        </Field>
        <Field label="Your email" hint="Optional. Lets you reset your password later if you forget it.">
          <input type="email" value={form.email} onChange={set("email")}
                 placeholder="sam@example.com" />
        </Field>
        <Field label="Time zone" hint="Allowance weeks start and end here.">
          <input value={form.timezone} onChange={set("timezone")} required />
        </Field>
        <button type="submit" disabled={busy} style={{ marginTop: 6 }}>
          {busy ? "Creating…" : "Create family"}
        </button>
      </form>
      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600, marginTop: 16 }}>
          {error}
        </p>
      )}
    </Centered>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontWeight: 600, fontSize: 14 }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{hint}</span>}
    </label>
  );
}
